import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { fetchAdminUsers } from '../lib/apiClient';
import { useAuth } from '../lib/auth';
import type { AdminUserListResponse, AdminUserRow } from '../types';

const numberFormatter = new Intl.NumberFormat('en-US');

type FiltersState = {
  email: string;
  status: string;
  isAdmin: 'true' | 'false' | 'both';
  page: number;
  limit: number;
};

const initialFilters: FiltersState = {
  email: '',
  status: '',
  isAdmin: 'both',
  page: 1,
  limit: 10,
};

export function AdminUsersPage(): JSX.Element {
  const { user } = useAuth();
  const [filters, setFilters] = useState<FiltersState>(initialFilters);
  const isSuperAdmin = user?.role === 'superadmin';

  const handleFilterChange = <K extends keyof FiltersState>(key: K, value: FiltersState[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: key === 'page' ? (value as number) : 1 }));
  };

  const queryResult = useQuery<AdminUserListResponse>({
    queryKey: ['admin-users', filters],
    queryFn: () =>
      fetchAdminUsers({
        email: filters.email || undefined,
        status: filters.status || undefined,
        isAdmin: filters.isAdmin,
        page: filters.page,
        limit: filters.limit,
      }),
    placeholderData: (previousData) => previousData,
    enabled: Boolean(isSuperAdmin),
  });

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isSuperAdmin) {
    return <Navigate to="/app" replace />;
  }

  const tableRows: AdminUserRow[] = queryResult.data?.results ?? [];
  const totalPages = queryResult.data?.totalPages ?? 1;

  const adminCount = tableRows.filter((row) => row.isAdmin).length;

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Super admin</p>
        <h1 className="text-2xl font-semibold text-foreground">User management</h1>
        <p className="text-sm text-muted-foreground">Review organisation accounts, billing status, and permissions.</p>
      </header>

      <section className="glass-card p-5">
        <div className="grid gap-4 md:grid-cols-4">
          <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">
            Email
            <input
              type="text"
              value={filters.email}
              onChange={(event) => handleFilterChange('email', event.target.value)}
              placeholder="dude@example.com"
              className="focus-ring rounded-xl border border-white/20 bg-white/60 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground dark:bg-white/5"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">
            Status
            <select
              value={filters.status}
              onChange={(event) => handleFilterChange('status', event.target.value)}
              className="focus-ring rounded-xl border border-white/20 bg-white/60 px-3 py-2 text-sm text-foreground dark:bg-white/5"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="trialing">Trialing</option>
              <option value="past_due">Past due</option>
              <option value="canceled">Canceled</option>
              <option value="cancel_at_period_end">Cancel at period end</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">
            Is Admin
            <select
              value={filters.isAdmin}
              onChange={(event) => handleFilterChange('isAdmin', event.target.value as FiltersState['isAdmin'])}
              className="focus-ring rounded-xl border border-white/20 bg-white/60 px-3 py-2 text-sm text-foreground dark:bg-white/5"
            >
              <option value="both">Both</option>
              <option value="true">Admins only</option>
              <option value="false">Customers only</option>
            </select>
          </label>
          <div className="flex items-end justify-end">
            <button
              type="button"
              className="btn-glass btn-secondary focus-ring text-sm font-semibold"
              onClick={() => setFilters(initialFilters)}
            >
              Reset filters
            </button>
          </div>
        </div>
      </section>

      <section className="glass-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4 text-sm text-muted-foreground">
          <p>
            Page {filters.page} / {totalPages} · Showing {tableRows.length} of {numberFormatter.format(queryResult.data?.totalResults ?? 0)} users
          </p>
          <p>{adminCount} admins in view</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-white/10 text-sm">
            <thead className="bg-white/20 text-left uppercase tracking-[0.3em] text-xs text-muted-foreground dark:bg-white/5">
              <tr>
                <th className="px-6 py-3 font-semibold">Email / Username</th>
                <th className="px-6 py-3 font-semibold">Subscription Status</th>
                <th className="px-6 py-3 font-semibold">Stripe ID</th>
                <th className="px-6 py-3 font-semibold">Is Admin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {queryResult.isLoading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-6 text-center text-muted-foreground">
                    Loading user data…
                  </td>
                </tr>
              ) : tableRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-6 text-center text-muted-foreground">
                    No users match the filters.
                  </td>
                </tr>
              ) : (
                tableRows.map((row) => (
                  <tr key={row.id} className="bg-white/5 hover:bg-white/10">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-foreground">{row.email}</div>
                      <div className="text-xs text-muted-foreground">{row.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
                        {row.subscriptionStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs text-muted-foreground">Customer: {row.stripeCustomerId ?? '—'}</div>
                      <div className="text-xs text-muted-foreground">Subscription: {row.stripeSubscriptionId ?? '—'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs font-semibold text-foreground">{row.isAdmin ? 'admin' : 'customer'}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-white/10 px-6 py-4 text-sm">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-glass btn-secondary focus-ring"
              disabled={filters.page <= 1 || queryResult.isFetching}
              onClick={() => handleFilterChange('page', filters.page - 1)}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn-glass btn-secondary focus-ring"
              disabled={filters.page >= totalPages || queryResult.isFetching}
              onClick={() => handleFilterChange('page', filters.page + 1)}
            >
              Next
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Per page</span>
            <select
              value={filters.limit}
              onChange={(event) => handleFilterChange('limit', Number(event.target.value) as FiltersState['limit'])}
              className="focus-ring rounded-xl border border-white/20 bg-white/60 px-3 py-2 text-sm text-foreground dark:bg-white/5"
            >
              {[10, 20, 50].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>
    </div>
  );
}

